import { Injectable } from "@angular/core";
import { MsalService } from "@azure/msal-angular";
import { AccountInfo } from "@azure/msal-browser";
import * as t from "io-ts";
import { firstValueFrom, map, ReplaySubject, switchMap } from "rxjs";
import { UserInfoDTO } from "src/app/api/api-types";
import { PartnersService } from "src/app/partner/services/partners/partners.service";
import { decodeOrElseNull } from "src/utils";
import { mapClaimsToPermissions } from "./map-claims-to-permissions";
import { Permission } from "./permissions";

@Injectable({ providedIn: "root" })
export class UserService implements DetailsProperty<UserDetails> {
  public constructor(
    private readonly msal: MsalService,
    private readonly partners: PartnersService,
  ) {}

  public get details(): UserDetails {
    // The root component and route guards ensures that the user is loaded and
    // valid, so this is safe to assert as non-null for all usages after that.

    return this.#details!;
  }
  #details: UserDetails | null = null;

  public get organizationDetails(): OrganizationDetails {
    // The root component and route guards ensures that the user is loaded and
    // valid, so this is safe to assert as non-null for all usages after that.

    return this.#organizationDetails!;
  }
  #organizationDetails: OrganizationDetails | null = null;

  public get registrationDetails(): UserRegistrationDetails {
    // The root component and route guards ensures that the user is loaded and
    // valid, so this is safe to assert as non-null for all usages after that.

    return this.#registrationDetails!;
  }
  #registrationDetails: UserRegistrationDetails | null = null;

  readonly #isLoadedNotifier = new ReplaySubject<true>(1);

  readonly #isRegistrationDetailsLoadedNotifier = new ReplaySubject<true>(1);
  public readonly isLoaded = firstValueFrom(
    this.#isLoadedNotifier.pipe(
      // Wait for the user registration details to be loaded before completing.
      switchMap(() => this.#isRegistrationDetailsLoadedNotifier),
    ),
  );

  public get isRegistered(): boolean {
    return this.registrationDetails?.isRegistered ?? false;
  }

  public get isValid(): boolean {
    return this.#details !== null;
  }

  public get permissions(): ReadonlySet<Permission> {
    return this.#details?.permissions ?? new Set();
  }

  public readonly hasPolicyChanges = this.partners.changes.pipe(
    map((partnersAndSites) => partnersAndSites.length > 0),
  );

  public load(account: AccountInfo, userDetails: UserInfoDTO): void {
    this.#details = getDetails(userDetails);
    this.#organizationDetails = getUserOrganizationDetails(account);
    this.#isLoadedNotifier.next(true);
  }

  public async loadRegistrationDetails(details: UserInfoDTO): Promise<void> {
    const hasPolicy = await firstValueFrom(this.hasPolicyChanges);
    this.#registrationDetails = { ...details, hasPolicy };
    this.#isRegistrationDetailsLoadedNotifier.next(true);
  }

  public logout(): void {
    this.msal.logout();
    localStorage.clear();
  }

  public updateUserPermissions(userSiteRole: string): void {
    if (!this.#details) {
      return;
    }

    this.#details.permissions = mapClaimsToPermissions(userSiteRole);
  }
}

function getDetails(userDetails: UserInfoDTO): UserDetails {
  if (!userDetails.email) {
    throw new Error("User email is missing");
  }

  return {
    displayName: userDetails.displayName,
    emailAddress: userDetails.email,
    permissions: new Set(), // set initial empty permissions
  };
}

interface DetailsProperty<T> {
  readonly details: T;
}

export interface UserDetails {
  readonly displayName: string;
  readonly emailAddress: string;
  permissions: ReadonlySet<Permission>;
}

export interface UserRegistrationDetails {
  readonly hasPolicy: boolean;
  readonly isRegistered: boolean;
}

function getUserOrganizationDetails(
  account: AccountInfo,
): OrganizationDetails | null {
  const idToken = decodeRegistrationToken(account.idTokenClaims);
  if (!idToken) {
    return null;
  }

  return {
    organizationName: idToken.extension_Signup_ExternalOrgName || null,
  };
}

interface OrganizationDetails {
  readonly organizationName: string | null;
}

const registrationTokenClaimsCodec = t.partial({
  extension_Signup_ExternalOrgName: t.string,
});

const decodeRegistrationToken = decodeOrElseNull(registrationTokenClaimsCodec);
